"use client";

import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { useServerInsertedHTML } from "next/navigation";
import { useState, type PropsWithChildren, type ReactNode } from "react";

type EmotionCacheState = {
  cache: ReturnType<typeof createCache>;
  flush: () => string[];
};

export function EmotionRegistry({ children }: PropsWithChildren): ReactNode {
  const [{ cache, flush }] = useState<EmotionCacheState>(() => {
    const emotionCache = createCache({ key: "css" });
    emotionCache.compat = true;

    const originalInsert = emotionCache.insert;
    let insertedNames: string[] = [];

    emotionCache.insert = (...args) => {
      const serialized = args[1];

      if (emotionCache.inserted[serialized.name] === undefined) {
        insertedNames.push(serialized.name);
      }

      return originalInsert(...args);
    };

    return {
      cache: emotionCache,
      flush: () => {
        const names = insertedNames;
        insertedNames = [];
        return names;
      },
    };
  });

  useServerInsertedHTML(() => {
    const names = flush();
    if (names.length === 0) return null;

    const styles = names
      .map((name) => cache.inserted[name])
      .filter((style): style is string => typeof style === "string")
      .join("");

    return (
      <style
        data-emotion={`${cache.key} ${names.join(" ")}`}
        dangerouslySetInnerHTML={{ __html: styles }}
      />
    );
  });

  return <CacheProvider value={cache}>{children}</CacheProvider>;
}
